import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import {
  Box, Paper, Tab, Tabs, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, TextField, Alert as MuiAlert,
  IconButton, Tooltip, Chip, InputLabel, Select,
  MenuItem, FormControl, CircularProgress, Typography,
  FormGroup, FormControlLabel, Checkbox, FormLabel, Divider,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { HeadlinePage, TableCustom } from 'components/common';
import { copyToClipboard } from 'utils';
import ApiTokenDocumentation from './ApiTokenDocumentation';

const AVAILABLE_SCOPES = [
  { value: 'jobs.run', label: 'Jobs: Run' },
  { value: 'jobs.read', label: 'Jobs: Read' },
  { value: 'credentials.read', label: 'Credentials: Read' },
  { value: 'inventories.read', label: 'Inventories: Read' },
  { value: 'projects.read', label: 'Projects: Read' },
  { value: 'templates.read', label: 'Templates: Read' },
];

// ─── Plain-token dialog ───────────────────────────────────────────────────────
const PlainTokenDialog = ({ plainToken, onClose }) => {
  const [t] = useTranslation();
  // 'idle' | 'success' | 'fallback' | 'error'. The visual indicator
  // lives next to the button (not in a Tooltip) so it stays visible
  // even after the user moves the mouse away from the button.
  //
  // 'fallback' is the insecure-context case: navigator.clipboard is
  // unavailable and execCommand('copy') returned true but cannot be
  // verified. We pre-select the token text so Ctrl+C / Cmd+C still
  // works without further user action.
  const [copyState, setCopyState] = useState('idle');
  const timeoutRef = useRef(null);
  const tokenTextRef = useRef(null);

  // Cancel any pending reset when the dialog unmounts.
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // On 'fallback', pre-select the token text so the user only needs to
  // press Ctrl+C / Cmd+C. We don't do this on 'error' because the
  // selection itself might be the problem; instead we show the manual
  // hint in the alert.
  useEffect(() => {
    if (copyState !== 'fallback' || !tokenTextRef.current) return;
    const node = tokenTextRef.current;
    const range = document.createRange();
    range.selectNodeContents(node);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  }, [copyState]);

  const handleCopy = async () => {
    // A rapid second click should not let the previous timeout cancel
    // the new indicator prematurely.
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    const result = await copyToClipboard(plainToken);
    if (!result.ok) {
      // Surface the failure in the console so the user can verify in
      // DevTools that the clipboard write actually failed.
      // eslint-disable-next-line no-console
      console.warn('[PlainTokenDialog] clipboard copy failed:', result.reason);
    }
    setCopyState(result.mode);
    // Fallback gets a longer window so the user has time to read the
    // hint and press Ctrl+C. Success/error get the original 3s.
    const resetMs = result.mode === 'fallback' ? 6000 : 3000;
    timeoutRef.current = setTimeout(() => {
      setCopyState('idle');
      timeoutRef.current = null;
    }, resetMs);
  };

  const isSuccess = copyState === 'success';
  const isFallback = copyState === 'fallback';
  const isError = copyState === 'error';
  const hasFeedback = copyState !== 'idle';

  let feedbackColor = 'text.primary';
  let feedbackLabel = '';
  if (isSuccess) {
    feedbackColor = 'success.main';
    feedbackLabel = t('common.copied');
  } else if (isFallback) {
    feedbackColor = 'warning.main';
    feedbackLabel = t('common.copyFallback');
  } else if (isError) {
    feedbackColor = 'error.main';
    feedbackLabel = t('common.copyError');
  }

  return (
    <Dialog open={!!plainToken} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('apiTokenList.components.plainTokenDialog.title')}</DialogTitle>
      <DialogContent>
        <MuiAlert severity="warning" sx={{ mb: 2 }}>
          {t('apiTokenList.components.plainTokenDialog.warning')}
        </MuiAlert>
        {(isFallback || isError) && (
          <MuiAlert severity={isError ? 'error' : 'warning'} sx={{ mb: 2 }}>
            {t('common.copyFallbackHint')}
          </MuiAlert>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'grey.100', p: 1.5, borderRadius: 1 }}>
          <Typography
            ref={tokenTextRef}
            variant="body2"
            sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}
          >
            {plainToken}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 150, justifyContent: 'flex-end' }}>
            {hasFeedback && (
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  color: feedbackColor,
                }}
              >
                {feedbackLabel}
              </Typography>
            )}
            <Tooltip title={copyState === 'idle' ? t('common.copy') : ''}>
              <IconButton
                onClick={handleCopy}
                size="small"
                color={isSuccess ? 'success' : isError ? 'error' : isFallback ? 'warning' : 'default'}
                aria-label={t('common.copy')}
              >
                {isSuccess
                  ? <CheckCircleOutlineIcon fontSize="small" />
                  : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">{t('common.components.buttons.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Create Client dialog ──────────────────────────────────────────────────────
const ClientFormDialog = ({ open, onClose, onSubmit }) => {
  const [t] = useTranslation();
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const handleClose = () => { reset(); onClose(); };
  const handleCreate = (data) => { onSubmit(data); reset(); };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('apiTokenList.components.clientDialog.title')}</DialogTitle>
      <form onSubmit={handleSubmit(handleCreate)}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label={t('common.components.table.col.name')}
            {...register('name', { required: true })}
            error={!!errors.name}
            helperText={errors.name ? t('common.required') : ''}
            size="small"
            fullWidth
            autoFocus
          />
          <TextField
            label={t('common.components.table.col.description')}
            {...register('description')}
            size="small"
            fullWidth
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('common.components.buttons.cancel')}</Button>
          <Button type="submit" variant="contained">{t('common.components.buttons.create')}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// ─── Create Token dialog ───────────────────────────────────────────────────────
const TokenFormDialog = ({ open, onClose, onSubmit, clients, projects, templates }) => {
  const [t] = useTranslation();
  const { register, handleSubmit, reset, control, watch, setValue, getValues, formState: { errors } } = useForm({
    defaultValues: { expires_days: '365', scopes: ['jobs.run'], allowed_projects: [], allowed_templates: [] },
  });

  // Templates are filtered by the selected projects. If no projects are
  // selected, the template list is empty (and the Select shows a hint
  // asking the user to pick projects first).
  const watchedProjects = watch('allowed_projects', []);

  const filteredTemplates = useMemo(() => {
    if (!Array.isArray(watchedProjects) || watchedProjects.length === 0) return [];
    const projectSet = new Set(watchedProjects);
    return templates.filter((tmpl) => projectSet.has(tmpl.proyect_id));
  }, [watchedProjects, templates]);

  // If the user removes a project, any template that was only allowed by
  // that project would leave the token in an inconsistent state. Auto-prune
  // those selections so the form value always matches the visible options.
  useEffect(() => {
    const validIds = new Set(filteredTemplates.map((t) => t.id));
    const current = getValues('allowed_templates') || [];
    if (!Array.isArray(current)) return;
    const pruned = current.filter((id) => validIds.has(id));
    if (pruned.length !== current.length) {
      setValue('allowed_templates', pruned, { shouldDirty: true });
    }
  }, [filteredTemplates, getValues, setValue]);

  const handleScopeToggle = (scopeValue, onChange, currentScopes) => {
    const current = currentScopes || ['jobs.run'];
    if (current.includes(scopeValue)) {
      const filtered = current.filter((s) => s !== scopeValue);
      onChange(filtered.length > 0 ? filtered : ['jobs.run']);
    } else {
      onChange([...current, scopeValue]);
    }
  };

  const handleClose = () => { reset(); onClose(); };
  const handleCreate = (data) => {
    // scopes, allowed_projects, and allowed_templates already come as arrays from the form
    const scopes = data.scopes || ['jobs.run'];
    const allowed_projects = Array.isArray(data.allowed_projects) ? data.allowed_projects : [];
    const allowed_templates = Array.isArray(data.allowed_templates) ? data.allowed_templates : [];
    onSubmit({ ...data, scopes, allowed_projects, allowed_templates });
    reset();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('apiTokenList.components.tokenDialog.title')}</DialogTitle>
      <form onSubmit={handleSubmit(handleCreate)}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl size="small" fullWidth error={!!errors.client_id}>
            <InputLabel>{t('apiTokenList.components.tokenDialog.client')}</InputLabel>
            <Select
              label={t('apiTokenList.components.tokenDialog.client')}
              defaultValue=""
              {...register('client_id', { required: true })}
            >
              {clients.map((c) => (
                <MenuItem key={c.client_id} value={c.client_id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Controller
            name="scopes"
            control={control}
            defaultValue={['jobs.run']}
            render={({ field }) => (
              <FormControl component="fieldset" fullWidth error={!!errors.scopes}>
                <FormLabel component="legend" sx={{ fontSize: '0.875rem', color: 'rgba(0,0,0,0.6)', mb: 0.5 }}>
                  {t('apiTokenList.components.tokenDialog.scopes')}
                </FormLabel>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {AVAILABLE_SCOPES.map((scope) => (
                    <Chip
                      key={scope.value}
                      label={t(`apiTokenList.components.scopes.${scope.value.replace('.', '_')}`) || scope.label}
                      onClick={() => handleScopeToggle(scope.value, field.onChange, field.value)}
                      onDelete={() => {}}
                      deleteIcon={<span />}
                      color={field.value?.includes(scope.value) ? 'primary' : 'default'}
                      variant={field.value?.includes(scope.value) ? 'filled' : 'outlined'}
                      clickable
                    />
                  ))}
                </Box>
              </FormControl>
            )}
          />

          <Divider />

          <FormControl size="small" fullWidth>
            <InputLabel>{t('apiTokenList.components.tokenDialog.allowedProjects')}</InputLabel>
            <Controller
              name="allowed_projects"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <Select
                  {...field}
                  label={t('apiTokenList.components.tokenDialog.allowedProjects')}
                  multiple
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          {t('apiTokenList.components.tokenDialog.noRestriction')}
                        </Typography>
                      )}
                      {selected.map((id) => {
                        const proj = projects.find((p) => p.id === id);
                        return (
                          <Chip key={id} label={proj?.name || id} size="small" />
                        );
                      })}
                    </Box>
                  )}
                >
                  {projects.length === 0 ? (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        {t('apiTokenList.components.tokenDialog.loadingProjects')}
                      </Typography>
                    </MenuItem>
                  ) : (
                    projects.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        <Checkbox checked={field.value?.includes(p.id) || false} sx={{ py: 0 }} />
                        {p.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              )}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('apiTokenList.components.tokenDialog.projectsHelp')}
            </Typography>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>{t('apiTokenList.components.tokenDialog.allowedTemplates')}</InputLabel>
            <Controller
              name="allowed_templates"
              control={control}
              defaultValue={[]}
              render={({ field }) => {
                // Pick the right empty-state message based on what's missing.
                let emptyMessage = null;
                if (templates.length === 0) {
                  emptyMessage = t('apiTokenList.components.tokenDialog.loadingTemplates');
                } else if (!Array.isArray(watchedProjects) || watchedProjects.length === 0) {
                  emptyMessage = t('apiTokenList.components.tokenDialog.selectProjectFirst');
                } else if (filteredTemplates.length === 0) {
                  emptyMessage = t('apiTokenList.components.tokenDialog.noTemplatesForProjects');
                }
                return (
                  <Select
                    {...field}
                    label={t('apiTokenList.components.tokenDialog.allowedTemplates')}
                    multiple
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            {t('apiTokenList.components.tokenDialog.noRestriction')}
                          </Typography>
                        )}
                        {selected.map((id) => {
                          const tmpl = filteredTemplates.find((t) => t.id === id);
                          return (
                            <Chip key={id} label={tmpl?.name || id} size="small" />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {emptyMessage ? (
                      <MenuItem disabled>
                        <Typography variant="body2" color="text.secondary">
                          {emptyMessage}
                        </Typography>
                      </MenuItem>
                    ) : (
                      filteredTemplates.map((tmpl) => (
                        <MenuItem key={tmpl.id} value={tmpl.id}>
                          <Checkbox checked={field.value?.includes(tmpl.id) || false} sx={{ py: 0 }} />
                          {tmpl.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                );
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('apiTokenList.components.tokenDialog.templatesHelp')}
            </Typography>
          </FormControl>

          <TextField
            label={t('apiTokenList.components.tokenDialog.expiresDays')}
            type="number"
            {...register('expires_days')}
            size="small"
            fullWidth
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('common.components.buttons.cancel')}</Button>
          <Button type="submit" variant="contained">{t('common.components.buttons.create')}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// ─── Main Dumb component ───────────────────────────────────────────────────────
const ApiTokenListDumb = ({
  tab, onTabChange,
  // clients
  clients, openClientModal, onOpenClientModal, onCloseClientModal, onCreateClient, onToggleClient, onDeleteClient,
  // tokens
  tokens, clients_for_select, filterClientId, onFilterClientTokens,
  openTokenModal, onOpenTokenModal, onCloseTokenModal, onCreateToken, onRevokeToken, onDeleteToken,
  newPlainToken, onCloseNewToken,
  // projects & templates
  projects, templates,
  // audit
  auditLog, auditFilterClientId, onFilterAudit, clients_for_audit,
  // shared
  isLoaded,
}) => {
  const [t] = useTranslation();

  // ── Clients tab ────────────────────────────────────────────────────────────
  const clientColumns = [
    { label: t('common.components.table.col.name'), key: 'name' },
    { label: t('common.components.table.col.description'), key: 'description' },
    {
      label: t('common.components.table.col.status'),
      key: 'is_active',
      render: (row) => (
        <Chip
          label={row.is_active ? t('common.active') : t('common.inactive')}
          color={row.is_active ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    { label: t('common.components.table.col.createdAt'), key: 'created_at', render: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : '-' },
  ];

  const clientActions = [
    {
      label: t('apiTokenList.components.actions.toggle'),
      icon: <CheckCircleOutlineIcon color="primary" />,
      handle: onToggleClient,
      getIcon: (row) => row.is_active
        ? <PauseCircleOutlineIcon color="primary" />
        : <CheckCircleOutlineIcon color="success" />,
    },
    {
      label: t('apiTokenList.components.actions.deleteClient'),
      icon: <DeleteOutlinedIcon color="error" />,
      handle: onDeleteClient,
    },
  ];

  // ── Tokens tab ─────────────────────────────────────────────────────────────
  const SCOPE_COLORS = {
    'jobs.run': 'primary',
    'jobs.read': 'info',
    'credentials.read': 'warning',
    'inventories.read': 'secondary',
    'projects.read': 'success',
    'templates.read': 'default',
  };

  const renderScopeChip = (scope) => {
    const color = SCOPE_COLORS[scope] || 'default';
    return (
      <Chip
        key={scope}
        size="small"
        label={t(`apiTokenList.components.scopes.${scope.replace('.', '_')}`, scope)}
        color={color}
        variant="outlined"
        sx={{ mr: 0.5, mb: 0.5 }}
      />
    );
  };

  const tokenColumns = [
    { label: t('apiTokenList.components.table.prefix'), key: 'token_prefix' },
    { label: t('apiTokenList.components.table.client'), key: 'client_name' },
    {
      label: t('apiTokenList.components.table.scopes'),
      key: 'scopes',
      render: (row) => {
        const scopes = Array.isArray(row.scopes) ? row.scopes : [];
        if (scopes.length === 0) return <Chip size="small" label={t('common.none')} variant="outlined" />;
        return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{scopes.map(renderScopeChip)}</Box>;
      },
    },
    {
      label: t('common.components.table.col.status'),
      key: 'is_revoked',
      render: (row) => {
        if (row.revoked_at) {
          return <Chip size="small" label={t('apiTokenList.components.table.revoked')} color="error" variant="outlined" />;
        }
        if (row.expires_at && new Date(row.expires_at) < new Date()) {
          return <Chip size="small" label={t('apiTokenList.components.table.expired')} color="warning" variant="outlined" />;
        }
        return <Chip size="small" label={t('apiTokenList.components.table.active')} color="success" variant="outlined" />;
      },
    },
    { label: t('apiTokenList.components.table.expiresAt'), key: 'expires_at', render: (row) => row.expires_at ? new Date(row.expires_at).toLocaleDateString() : '-' },
    { label: t('apiTokenList.components.table.lastUsed'), key: 'last_used_at', render: (row) => row.last_used_at ? new Date(row.last_used_at).toLocaleString() : '-' },
  ];

  const tokenActions = [
    {
      label: t('apiTokenList.components.actions.revoke'),
      icon: <BlockOutlinedIcon color="error" />,
      handle: onRevokeToken,
      isDisabled: (row) => !!row.revoked_at,
    },
    {
      label: t('apiTokenList.components.actions.delete'),
      icon: <DeleteOutlinedIcon color="error" />,
      handle: onDeleteToken,
    },
  ];

  // ── Audit tab ──────────────────────────────────────────────────────────────
  const auditColumns = [
    { label: t('apiTokenList.components.table.requestId'), key: 'request_id', render: (row) => row.request_id?.substring(0, 8) + '…' },
    { label: t('apiTokenList.components.table.method'), key: 'method' },
    { label: t('apiTokenList.components.table.path'), key: 'path' },
    { label: t('apiTokenList.components.table.status'), key: 'status_code' },
    { label: t('apiTokenList.components.table.ip'), key: 'ip_address' },
    { label: t('apiTokenList.components.table.duration'), key: 'duration_ms', render: (row) => row.duration_ms != null ? `${row.duration_ms} ms` : '-' },
    { label: t('common.components.table.col.createdAt'), key: 'created_at', render: (row) => row.created_at ? new Date(row.created_at).toLocaleString() : '-' },
  ];

  return (
    <>
      <HeadlinePage
        title="apiTokenList.headline"
        buttonLabel={tab === 0 ? 'apiTokenList.components.addClient' : tab === 1 ? 'apiTokenList.components.addToken' : null}
        handleOnClick={tab === 0 ? onOpenClientModal : tab === 1 ? onOpenTokenModal : null}
      />

      <Paper sx={{ mt: 2 }}>
        <Tabs
          value={tab}
          onChange={onTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label={t('apiTokenList.components.tabs.clients')} />
          <Tab label={t('apiTokenList.components.tabs.tokens')} />
          <Tab label={t('apiTokenList.components.tabs.audit')} />
          <Tab label={t('apiTokenList.components.tabs.documentation')} />
        </Tabs>

        {/* ── Clients ── */}
        {tab === 0 && (
          <Box sx={{ p: 2 }}>
            <TableCustom
              title="apiTokenList.components.table.clientsTitle"
              columns={clientColumns}
              content={clients}
              actions={clientActions}
              loading={isLoaded.get}
              handleNotResults={onOpenClientModal}
            />
          </Box>
        )}

        {/* ── Tokens ── */}
        {tab === 1 && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel>{t('apiTokenList.components.filterByClient')}</InputLabel>
                <Select
                  label={t('apiTokenList.components.filterByClient')}
                  value={filterClientId}
                  onChange={(e) => onFilterClientTokens(e.target.value)}
                >
                  <MenuItem value="">{t('common.all')}</MenuItem>
                  {clients_for_select.map((c) => (
                    <MenuItem key={c.client_id} value={c.client_id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TableCustom
              title="apiTokenList.components.table.tokensTitle"
              columns={tokenColumns}
              content={tokens}
              actions={tokenActions}
              loading={isLoaded.get}
              handleNotResults={onOpenTokenModal}
            />
          </Box>
        )}

        {/* ── Audit ── */}
        {tab === 2 && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel>{t('apiTokenList.components.filterByClient')}</InputLabel>
                <Select
                  label={t('apiTokenList.components.filterByClient')}
                  value={auditFilterClientId}
                  onChange={(e) => onFilterAudit(e.target.value)}
                >
                  <MenuItem value="">{t('common.all')}</MenuItem>
                  {clients_for_audit.map((c) => (
                    <MenuItem key={c.client_id} value={c.client_id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {isLoaded.get
              ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
              : <TableCustom
                  title="apiTokenList.components.table.auditTitle"
                  columns={auditColumns}
                  content={auditLog}
                  actions={[]}
                  loading={false}
                />
            }
          </Box>
        )}

        {/* ── Documentation ── */}
        {tab === 3 && <ApiTokenDocumentation />}
      </Paper>

      {/* ── Dialogs ── */}
      <ClientFormDialog open={openClientModal} onClose={onCloseClientModal} onSubmit={onCreateClient} />
      <TokenFormDialog
        open={openTokenModal}
        onClose={onCloseTokenModal}
        onSubmit={onCreateToken}
        clients={clients_for_select}
        projects={projects}
        templates={templates}
      />
      <PlainTokenDialog plainToken={newPlainToken} onClose={onCloseNewToken} />
    </>
  );
};

export default ApiTokenListDumb;
